/*
 * PRD-037 — Paso "General" del wizard unificado "Preparar comercio" (app SEPARADA, V4 inline).
 *
 * Reemplaza al step Tailwind `GeneralInfo` del panel (acoplado a @plick/ui + InputWLabel +
 * SelectDropDown, que rompían el layout en móvil — bug #25). Captura los datos generales sobre el
 * mismo modelo del Commerce con los primitivos V4 inline-style del portal: WhatsApp de pedidos
 * (PhoneField, +52 fijo), prefijo de folio (FieldV4 con preview "-00000") y zona horaria (Select V4)
 * + la hora actual derivada.
 *
 * Plick opera en MX: el WhatsApp se captura como 10 dígitos nacionales con lada +52 fija, así que
 * `country_code` se fija en 52 por defecto (lo que `validateCommerce` exige distinto de 0). El dueño
 * podrá ajustarlo luego desde el panel si hiciera falta.
 */
import { useEffect, useState } from 'react';
import type { Commerce } from '@interfaces/general';
import { type MissingCommerceKeys, useGetTimezones } from '@pages/Commerce/hooks/commerce';
import { FieldV4, TextInputV4 } from '@components/v4/Input/Input';
import { Select } from '@components/v4/Select/Select';
import { V4 } from '@components/v4/tokens';
import { C, PhoneField } from './ui';

const MX_COUNTRY_CODE = 52;

interface Props {
  commerce: Commerce;
  missingValues: MissingCommerceKeys[];
  handleUpdateValue: <K extends keyof Commerce>(key: K, value: Commerce[K]) => void;
}

export const ConfigureGeneralSection = ({ commerce, missingValues, handleUpdateValue }: Props) => {
  const { timeZones } = useGetTimezones();

  // Validación por-campo: el error rojo solo se muestra cuando el campo ya fue tocado (blur), NO al
  // montar. Así, al abrir el paso, ningún campo vacío aparece en rojo antes de que el usuario
  // interactúe (bug: errores "Requerido"/"WhatsApp válido" salían de entrada). `onBlur` en el wrapper
  // marca el campo como tocado (React lo propaga desde el input hijo vía focusout).
  const [touched, setTouched] = useState<Partial<Record<MissingCommerceKeys, boolean>>>({});
  const markTouched = (key: MissingCommerceKeys) => setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  const showError = (key: MissingCommerceKeys) => Boolean(touched[key]) && missingValues.includes(key);

  // Defaults sensatos para el borrador MX: lada +52 y zona horaria de CDMX (igual que GeneralInfo).
  useEffect(() => {
    if (!commerce.country_code) handleUpdateValue('country_code', MX_COUNTRY_CODE);
  }, [commerce.country_code, handleUpdateValue]);
  useEffect(() => {
    if (!commerce.zona_horaria) handleUpdateValue('zona_horaria', 'America/Mexico_City');
  }, [commerce.zona_horaria, handleUpdateValue]);

  const currentTime = commerce.zona_horaria
    ? new Date().toLocaleTimeString(undefined, { timeZone: commerce.zona_horaria }).replace(/(.*)\D\d+/, '$1')
    : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px' }}>
        Datos generales del negocio. El dueño solo los confirmará en su configuración inicial.
      </p>

      <div onBlur={() => markTouched('telefono')}>
        <PhoneField
          label="WhatsApp donde llegarán los pedidos"
          value={commerce.telefono ? String(commerce.telefono) : ''}
          onChange={(digits) => handleUpdateValue('telefono', digits ? Number(digits) : undefined)}
          error={showError('telefono') ? 'Ingresa un WhatsApp válido de 10 dígitos.' : undefined}
          required
        />
      </div>

      <div style={{ marginBottom: 14 }} onBlur={() => markTouched('prefijo_folio')}>
        <FieldV4
          label="Prefijo para el folio de pedidos:"
          required
          error={showError('prefijo_folio') ? 'Requerido' : undefined}
          helper="Aparece en cada ticket. Usa las iniciales del negocio."
        >
          <TextInputV4
            value={commerce.prefijo_folio || ''}
            onChange={(v) => handleUpdateValue('prefijo_folio', String(v).toUpperCase().slice(0, 5))}
            placeholder="Ej. ABCD"
            suffix="-00000"
            maxLength={5}
          />
        </FieldV4>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 200 }} onBlur={() => markTouched('zona_horaria')}>
          <FieldV4 label="Zona horaria:" required error={showError('zona_horaria') ? 'Requerido' : undefined}>
            <Select
              value={commerce.zona_horaria || ''}
              onChange={(v) => handleUpdateValue('zona_horaria', v)}
              options={timeZones}
              placeholder="Selecciona la zona horaria"
            />
          </FieldV4>
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <FieldV4 label="Hora actual:">
            <div
              style={{
                height: 41,
                display: 'flex',
                alignItems: 'center',
                padding: '0 12px',
                border: `1.5px solid ${C.cardBorder}`,
                borderRadius: 8,
                background: V4.grayUltraLight,
                color: C.primary,
                fontSize: 15,
                boxSizing: 'border-box',
              }}
            >
              {currentTime || '—'}
            </div>
          </FieldV4>
        </div>
      </div>
    </div>
  );
};

export default ConfigureGeneralSection;
